import React, { useCallback, useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import { Button, Heading, Icon } from "@loanpal/lumos__core";
import {
  createPaymentCustomer,
  createPaymentInvoice,
  createPaymentTransaction,
  getStates,
  resolveInvoiceLink,
  sendPaymentInvoiceEmail,
} from "../../utils/api";
import { numberToMoney, numberToMoney2, moneyStringToNumber } from "../../utils/format";
import { checkAndResolveSubmittingUserWithRetry } from "../../utils/submitting-user";
import { fetchPaymentsLocalActorOverride } from "../../utils/payments-local";
import { CUSTOMER_SUPPORT_PHONE } from "../../utils/glodbals";
import StreetAddressAutocomplete from "./StreetAddressAutocomplete";

const STEPS = ["Start new payment", "Build Your Order", "Create Invoice"];

/** Pay-only visual tokens (overrides Lumos defaults via sx — no Lumos package changes). */
const PAY = {
  radius: "12px",
  radiusSm: "10px",
  fieldHeight: 40,
  pageBg: "#f4f6f7",
  fieldBg: "#fafbfc",
  cardShadow:
    "0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.06)",
  focusRing: "0 0 0 3px rgba(0, 131, 143, 0.18)",
};

const fieldSx = {
  width: "100%",
  height: PAY.fieldHeight,
  px: 3,
  border: "1px solid",
  borderColor: "neutral.l3",
  borderRadius: PAY.radiusSm,
  fontSize: 2,
  boxSizing: "border-box",
  bg: PAY.fieldBg,
  color: "neutral.d1",
  transition:
    "border-color 150ms ease, box-shadow 150ms ease, background 150ms ease",
  "&:hover": {
    borderColor: "neutral.l2",
  },
  "&:focus": {
    outline: "none",
    borderColor: "primary.base",
    bg: "white",
    boxShadow: PAY.focusRing,
  },
};

const labelSx = {
  display: "block",
  mb: "6px",
  fontSize: 2,
  fontWeight: 500,
  color: "neutral.d1",
  letterSpacing: "0.01em",
};

const primaryButtonSx = {
  borderRadius: PAY.radiusSm,
  height: 40,
  px: 3,
  fontWeight: 600,
};

const secondaryButtonSx = {
  borderRadius: PAY.radiusSm,
  height: 40,
  px: 3,
  fontWeight: 600,
};

const cardSx = {
  border: "1px solid",
  borderColor: "neutral.l3",
  borderRadius: PAY.radius,
  p: [3, 4],
  mb: 3,
  bg: "white",
  boxShadow: PAY.cardShadow,
};

function ProgressStepper({ activeStep }) {
  return (
    <Flex
      sx={{
        alignItems: "center",
        width: "100%",
        mb: 4,
        px: [3, 4],
        py: 3,
        bg: "white",
        borderRadius: PAY.radius,
        border: "1px solid",
        borderColor: "neutral.l3",
        boxShadow: PAY.cardShadow,
        gap: 0,
        flexWrap: ["wrap", "nowrap"],
        rowGap: 3,
      }}
      aria-label="Payment steps"
    >
      {STEPS.map((label, i) => {
        const isActive = i === activeStep;
        const isComplete = i < activeStep;
        const isCurrentOrDone = isActive || isComplete;
        return (
          <React.Fragment key={label}>
            <Flex
              sx={{
                alignItems: "center",
                gap: 2,
                flex: "0 0 auto",
                minWidth: 0,
                bg: "white",
                zIndex: 1,
                px: "2px",
              }}
            >
              <Flex
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bg: isCurrentOrDone ? "primary.base" : "neutral.l3",
                  color: "white",
                  fontSize: 0,
                  fontWeight: 700,
                  lineHeight: 1,
                  boxShadow: isActive ? PAY.focusRing : "none",
                }}
                aria-current={isActive ? "step" : undefined}
              >
                {i + 1}
              </Flex>
              <Box
                sx={{
                  fontSize: 2,
                  fontWeight: isActive ? 600 : 500,
                  color: isCurrentOrDone ? "neutral.d1" : "neutral.d2",
                  whiteSpace: "nowrap",
                  bg: "white",
                }}
              >
                {label}
              </Box>
            </Flex>
            {i < STEPS.length - 1 ? (
              <Box
                sx={{
                  flex: "1 1 24px",
                  height: "2px",
                  minWidth: 16,
                  bg: isComplete ? "primary.l2" : "neutral.l3",
                  mx: 3,
                  borderRadius: "999px",
                  alignSelf: "center",
                }}
                aria-hidden
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </Flex>
  );
}

const backButtonSx = {
  mb: 3,
  color: "primary.base",
  border: "1px solid",
  borderColor: "primary.base",
  bg: "transparent",
  display: "inline-flex",
  alignItems: "center",
  gap: 2,
  px: 3,
  height: 40,
  borderRadius: PAY.radiusSm,
  fontWeight: 600,
  cursor: "pointer",
  transition: "background 150ms ease, border-color 150ms ease",
  "&:hover": {
    bg: "primary.l4",
    color: "primary.base",
    borderColor: "primary.base",
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "default",
  },
};

function BackButton({ onClick, disabled }) {
  return (
    <Button
      variant="ghost"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={backButtonSx}
    >
      <Box as="span" sx={{ color: "primary.base", lineHeight: 1 }} aria-hidden>
        ←
      </Box>
      Back
    </Button>
  );
}

function StepStatusIcon({ status }) {
  if (status === "complete") {
    return (
      <Icon
        variant="check-circle"
        size="sm"
        sx={{ color: "#15803d", flexShrink: 0 }}
      />
    );
  }
  if (status === "active") {
    return (
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: "2px solid",
          borderColor: "primary.base",
          overflow: "hidden",
          flexShrink: 0,
          position: "relative",
          bg: "white",
        }}
        aria-hidden
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "50%",
            height: "100%",
            bg: "primary.base",
          }}
        />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        border: "2px solid",
        borderColor: "neutral.l2",
        flexShrink: 0,
        bg: "white",
      }}
      aria-hidden
    />
  );
}

function SummaryDetailRow({ icon, children }) {
  return (
    <Flex
      sx={{
        alignItems: "flex-start",
        gap: 2,
        minHeight: 24,
        color: "neutral.d1",
        fontSize: 2,
      }}
    >
      <Box
        sx={{
          width: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          mt: "1px",
          color: "neutral.d2",
        }}
      >
        <Icon variant={icon} size="sm" />
      </Box>
      <Box sx={{ lineHeight: "20px", wordBreak: "break-word" }}>{children}</Box>
    </Flex>
  );
}

function NextStepBlock({ status, title, onEdit, children }) {
  const bg =
    status === "active"
      ? "rgba(0, 131, 143, 0.08)"
      : status === "complete"
        ? "#f3f4f6"
        : "#f9fafb";

  return (
    <Box
      sx={{
        bg,
        borderRadius: PAY.radiusSm,
        px: 3,
        py: 3,
        mb: 2,
      }}
    >
      <Flex sx={{ alignItems: "center", gap: 2, minHeight: 28 }}>
        <StepStatusIcon status={status} />
        <Box sx={{ flex: 1, fontWeight: 600, fontSize: 2, color: "neutral.d1" }}>
          {title}
        </Box>
        {status === "complete" && onEdit ? (
          <Button
            variant="ghost"
            type="button"
            aria-label={`Edit ${title}`}
            onClick={onEdit}
            sx={{
              p: 1,
              minWidth: 0,
              borderRadius: "8px",
              color: "neutral.d2",
              "&:hover": { color: "neutral.d1", bg: "rgba(0,0,0,0.04)" },
            }}
          >
            <Icon variant="edit" size="sm" />
          </Button>
        ) : null}
      </Flex>
      {children ? <Box sx={{ mt: 2, pl: "30px" }}>{children}</Box> : null}
    </Box>
  );
}

function Field({ id, label, description, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box as="label" htmlFor={id} sx={labelSx}>
        {label}
      </Box>
      {description ? (
        <Box sx={{ mb: 1, fontSize: 1, color: "neutral.d2" }}>{description}</Box>
      ) : null}
      {children}
    </Box>
  );
}

const emptyCustomerForm = () => ({
  email: "",
  firstName: "",
  lastName: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  referenceId: "",
  amount: "",
  description: "",
});

function actorFromSubmittingUser(user) {
  if (!user?.email) return undefined;
  return {
    email: String(user.email).trim(),
    firstName: String(user.firstName || "").trim() || "—",
    lastName: String(user.lastName || "").trim() || "—",
  };
}

function parseAmount(value) {
  const n = moneyStringToNumber(value);
  if (n == null || Number.isNaN(n) || !Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

function dollarsToCents(dollars) {
  return Math.round(dollars * 100);
}

async function copyTextToClipboard(text) {
  const value = String(text || "");
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through to legacy copy
  }
  try {
    const el = document.createElement("textarea");
    el.value = value;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

function formatAddress(form) {
  const street = [form.line1.trim(), form.line2.trim()].filter(Boolean).join(" ");
  const cityStateZip = [
    form.city.trim(),
    [form.state.trim(), form.postalCode.trim()].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return [street, cityStateZip].filter(Boolean).join(" ");
}

function normalizeStates(response) {
  const list = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : [];
  return list
    .map((s) => ({
      code: String(s?.code ?? ""),
      name: String(s?.name ?? s?.code ?? ""),
    }))
    .filter((s) => s.code);
}

/**
 * Multi-step payments invoice flow (ported from payments-temp).
 * Gate (paymentsEnabled) is handled by the parent pay page.
 * Actor is resolved from SharedApp.submittingUser (same verify/substitute logic as apply).
 */
export default function PaymentsFlow({
  sharedApp,
  clientId,
  elevatedToken,
  paymentsConfig,
  partnerName,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(emptyCustomerForm);
  const [actor, setActor] = useState(undefined);
  const [actorLoading, setActorLoading] = useState(true);
  const [actorError, setActorError] = useState("");
  const [states, setStates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [transactionResult, setTransactionResult] = useState(null);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareMethod, setShareMethod] = useState("manual"); // "email" | "manual"

  const hasAddressInput = Boolean(
    form.line1.trim() ||
      form.city.trim() ||
      form.state.trim() ||
      form.postalCode.trim()
  );

  const canContinueStep0 =
    Boolean(actor) &&
    Boolean(form.email.trim() && form.firstName.trim() && form.lastName.trim()) &&
    (!hasAddressInput ||
      Boolean(
        form.line1.trim() &&
          form.city.trim() &&
          form.state.trim() &&
          form.postalCode.trim()
      ));

  const canContinueStep1 =
    Boolean(form.description.trim()) && parseAmount(form.amount) !== null;

  const apiEnvironment =
    paymentsConfig?.apiEnvironment === "production" ? "production" : "sandbox";

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    getStates(clientId).then((statesData) => {
      if (!cancelled) setStates(normalizeStates(statesData));
    });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;

    async function resolvePaymentActor() {
      setActorLoading(true);
      setActorError("");
      setActor(undefined);

      const localActor = await fetchPaymentsLocalActorOverride();
      if (cancelled) return;
      if (localActor) {
        const nextActor = actorFromSubmittingUser(localActor);
        setActor(nextActor);
        setActorLoading(false);
        console.log(
          "[payments] actor for payment endpoints (payments-local.json override)",
          nextActor
        );
        return;
      }

      const configured = sharedApp?.submittingUser;
      const configuredEmail = configured?.email;
      if (!clientId || !configuredEmail) {
        if (!cancelled) {
          setActorError("SharedApp has no submittingUser email.");
          setActorLoading(false);
          console.log(
            "[payments] actor unresolved — missing SharedApp.submittingUser.email",
            { clientId, submittingUser: configured }
          );
        }
        return;
      }

      console.log(
        "[payments] resolving actor from SharedApp.submittingUser",
        configured
      );

      try {
        const result = await checkAndResolveSubmittingUserWithRetry(
          clientId,
          configuredEmail,
          elevatedToken
        );
        if (cancelled) return;

        let sourceUser = configured;
        if (result.status === "configured") {
          sourceUser = configured;
        } else if (result.status === "substituted" && result.substitute) {
          sourceUser = result.substitute;
        } else {
          setActorError(
            result.reason ||
              "Could not verify submitting user for payment actor."
          );
          setActor(undefined);
          console.log("[payments] actor resolve failed", result);
          return;
        }

        const nextActor = actorFromSubmittingUser(sourceUser);
        setActor(nextActor);
        console.log("[payments] actor for payment endpoints", nextActor, {
          resolveStatus: result.status,
          roleName: result.roleName,
          configuredEmail,
        });
      } catch (err) {
        if (!cancelled) {
          setActorError("Failed to resolve payment actor.");
          console.log("[payments] actor resolve error", err);
        }
      } finally {
        if (!cancelled) setActorLoading(false);
      }
    }

    resolvePaymentActor();
    return () => {
      cancelled = true;
    };
  }, [sharedApp, clientId, elevatedToken]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAmount = (raw) => {
    const digits = String(raw ?? "").replace(/[^0-9]/g, "");
    if (!digits) {
      setForm((prev) => ({ ...prev, amount: "" }));
      return;
    }
    const wholeDollars = Number(digits.slice(0, 9));
    if (!Number.isFinite(wholeDollars) || wholeDollars <= 0) {
      setForm((prev) => ({ ...prev, amount: "" }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      amount: numberToMoney(wholeDollars),
    }));
  };

  const handleStreetPlaceSelected = useCallback((address) => {
    setForm((prev) => ({
      ...prev,
      line1: address.line1,
      line2: address.line2 || prev.line2,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
    }));
  }, []);

  const clearForm = () => {
    setForm(emptyCustomerForm());
    setTransactionResult(null);
    setInvoiceResult(null);
    setSubmitError("");
    setLinkCopied(false);
    setShareMethod("manual");
    setActiveStep(0);
  };

  const handleContinue = () => {
    if (activeStep === 0 && canContinueStep0) setActiveStep(1);
    else if (activeStep === 1 && canContinueStep1) setActiveStep(2);
  };

  const handleBack = () => {
    setSubmitError("");
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const buildCustomerPayload = useCallback(() => {
    const payload = {
      email: form.email.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      actor,
    };
    if (hasAddressInput) {
      payload.address = {
        line1: form.line1.trim(),
        line2: form.line2.trim() || undefined,
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
      };
    }
    return payload;
  }, [actor, form, hasAddressInput]);

  const handleGenerateInvoiceLink = async () => {
    if (!actor || !clientId) return;
    const amount = parseAmount(form.amount);
    if (amount === null) return;

    setSubmitting(true);
    setSubmitError("");
    setTransactionResult(null);
    setInvoiceResult(null);
    setLinkCopied(false);

    console.log("[payments] submitting with actor", actor);

    try {
      const customer = await createPaymentCustomer(
        clientId,
        buildCustomerPayload(),
        elevatedToken
      );
      if (!customer?.id) throw new Error("Failed to create customer.");

      const transaction = await createPaymentTransaction(
        clientId,
        {
          amount: dollarsToCents(amount),
          customerId: customer.id,
          description: form.description.trim(),
          referenceNumber: form.referenceId.trim() || undefined,
          actor,
        },
        elevatedToken
      );
      if (!transaction?.id) throw new Error("Failed to create transaction.");
      setTransactionResult(transaction);

      const invoice = await createPaymentInvoice(
        clientId,
        { transactionId: transaction.id, actor },
        elevatedToken
      );
      if (!invoice?.id) throw new Error("Failed to create invoice.");

      const link = resolveInvoiceLink(invoice, apiEnvironment);

      if (shareMethod === "email" && form.email.trim()) {
        const customerName =
          `${form.firstName} ${form.lastName}`.trim() || "Customer";
        const invoiceNumber =
          form.referenceId.trim() ||
          transaction?.referenceNumber ||
          invoice?.referenceNumber ||
          invoice?.id;
        const dueLabel = new Date().toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
        await sendPaymentInvoiceEmail({
          toEmail: form.email.trim(),
          fromEmail: actor.email,
          partnerName: partnerName || "GoodLeap Payments",
          customerName,
          amountLabel: numberToMoney2(amount),
          description: form.description.trim(),
          invoiceNumber,
          invoiceUrl: link,
          dueLabel,
          contactEmail: actor.email,
          contactPhone: CUSTOMER_SUPPORT_PHONE || "",
        });
        setInvoiceResult({ ...invoice, url: link });
      } else {
        setInvoiceResult({ ...invoice, url: link });
        if (link) {
          const copied = await copyTextToClipboard(link);
          setLinkCopied(copied);
        }
      }
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to generate invoice link."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const invoiceLink = invoiceResult
    ? resolveInvoiceLink(invoiceResult, apiEnvironment)
    : null;

  const amountParsed = parseAmount(form.amount);
  const showNextSteps = activeStep >= 1 && !invoiceResult;

  return (
    <Box
      className="pay-flow"
      sx={{
        bg: PAY.pageBg,
        borderRadius: PAY.radius,
        p: [3, 4],
        mx: [-2, 0],
      }}
    >
      {!invoiceResult ? (
        <Heading variant="h5" sx={{ mb: 3, letterSpacing: "-0.02em" }}>
          Payment to {partnerName}
        </Heading>
      ) : null}

      {!invoiceResult ? <ProgressStepper activeStep={activeStep} /> : null}

      <Flex
        sx={{
          gap: 4,
          flexDirection: ["column", "column", showNextSteps ? "row" : "column"],
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: "100%",
            ...(invoiceResult
              ? {}
              : {
                  bg: "white",
                  borderRadius: PAY.radius,
                  border: "1px solid",
                  borderColor: "neutral.l3",
                  boxShadow: PAY.cardShadow,
                  p: [3, 4],
                }),
          }}
        >
          {activeStep === 0 && (
            <Box>
              {actorLoading ? (
                <Box sx={{ mb: 3, fontSize: 2, color: "neutral.d1" }}>
                  Resolving payment actor…
                </Box>
              ) : actorError ? (
                <Box sx={{ mb: 3, color: "danger.base", fontSize: 2 }}>
                  {actorError}
                </Box>
              ) : null}

              <Flex
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Heading variant="h5" as="h3" sx={{ fontSize: [4, 5], m: 0 }}>
                  Start New Payment
                </Heading>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={clearForm}
                  sx={{ borderRadius: PAY.radiusSm, fontWeight: 600 }}
                >
                  Clear form
                </Button>
              </Flex>

              <Heading variant="h6" as="h3" sx={{ mb: 2, fontSize: [2, 3] }}>
                Customer details
              </Heading>

              <Field id="pay-email" label="Email">
                <Box
                  as="input"
                  id="pay-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  sx={fieldSx}
                />
              </Field>
              <Flex sx={{ gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 200px" }}>
                  <Field id="pay-first" label="First name">
                    <Box
                      as="input"
                      id="pay-first"
                      value={form.firstName}
                      onChange={(e) => updateField("firstName", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
                <Box sx={{ flex: "1 1 200px" }}>
                  <Field id="pay-last" label="Last name">
                    <Box
                      as="input"
                      id="pay-last"
                      value={form.lastName}
                      onChange={(e) => updateField("lastName", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
              </Flex>

              <Heading variant="h6" as="h3" sx={{ mb: 2 }}>
                Project address{" "}
                <Box as="span" sx={{ fontWeight: 400, color: "neutral.d1" }}>
                  (optional)
                </Box>
              </Heading>

              <Box sx={{ mb: 3 }}>
                <StreetAddressAutocomplete
                  label="Street address"
                  value={form.line1}
                  onChange={(v) => updateField("line1", v)}
                  onPlaceSelected={handleStreetPlaceSelected}
                />
              </Box>
              <Field id="pay-line2" label="Address 2 (optional)">
                <Box
                  as="input"
                  id="pay-line2"
                  value={form.line2}
                  onChange={(e) => updateField("line2", e.target.value)}
                  sx={fieldSx}
                />
              </Field>
              <Flex sx={{ gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: "2 1 160px" }}>
                  <Field id="pay-city" label="City">
                    <Box
                      as="input"
                      id="pay-city"
                      value={form.city}
                      onChange={(e) => updateField("city", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
                <Box sx={{ flex: "1 1 100px" }}>
                  <Field id="pay-state" label="State">
                    <Box
                      as="select"
                      id="pay-state"
                      value={form.state}
                      onChange={(e) => updateField("state", e.target.value)}
                      sx={fieldSx}
                    >
                      <option value="">Select</option>
                      {states.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.code}
                        </option>
                      ))}
                    </Box>
                  </Field>
                </Box>
                <Box sx={{ flex: "1 1 100px" }}>
                  <Field id="pay-zip" label="ZIP">
                    <Box
                      as="input"
                      id="pay-zip"
                      value={form.postalCode}
                      onChange={(e) => updateField("postalCode", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
              </Flex>

              <Box
                sx={{
                  borderTop: "1px solid",
                  borderColor: "neutral.l3",
                  pt: 3,
                }}
              >
                <Field id="pay-ref" label="Order number (optional)">
                  <Box
                    as="input"
                    id="pay-ref"
                    value={form.referenceId}
                    onChange={(e) => updateField("referenceId", e.target.value)}
                    sx={fieldSx}
                  />
                </Field>
              </Box>

              <Flex sx={{ justifyContent: "flex-end", mt: 1 }}>
                <Button
                  type="button"
                  onClick={handleContinue}
                  disabled={!canContinueStep0 || actorLoading}
                  sx={primaryButtonSx}
                >
                  Continue
                </Button>
              </Flex>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <BackButton onClick={handleBack} />
              <Heading variant="h5" as="h3" sx={{ mb: 3, fontSize: [3, 4] }}>
                Build Your Order
              </Heading>
              <Field id="pay-amount" label="Amount (USD)">
                <Box
                  as="input"
                  id="pay-amount"
                  value={form.amount}
                  onChange={(e) => updateAmount(e.target.value)}
                  onBlur={() => {
                    const n = parseAmount(form.amount);
                    if (n != null) {
                      setForm((prev) => ({
                        ...prev,
                        amount: numberToMoney2(n),
                      }));
                    }
                  }}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="$0"
                  sx={fieldSx}
                />
              </Field>
              <Field id="pay-desc" label="Description">
                <Box
                  as="input"
                  id="pay-desc"
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  sx={fieldSx}
                />
              </Field>
              <Flex sx={{ justifyContent: "flex-end", mt: 1 }}>
                <Button
                  type="button"
                  onClick={handleContinue}
                  disabled={!canContinueStep1}
                  sx={primaryButtonSx}
                >
                  Continue
                </Button>
              </Flex>
            </Box>
          )}

          {activeStep === 2 && !invoiceResult && (
            <Box>
              <BackButton onClick={handleBack} disabled={submitting} />
              <Heading variant="h5" as="h3" sx={{ mb: 3, fontSize: [3, 4] }}>
                Create Invoice
              </Heading>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ ...labelSx, mb: 2 }}>Share method</Box>
                <Flex sx={{ gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                  <Box
                    as="label"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: "pointer",
                      fontSize: 2,
                    }}
                  >
                    <Box
                      as="input"
                      type="radio"
                      name="shareMethod"
                      checked={shareMethod === "email"}
                      onChange={() => setShareMethod("email")}
                      sx={{ accentColor: "primary.base", width: 18, height: 18 }}
                    />
                    Automatic email
                  </Box>
                  <Box
                    as="label"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: "pointer",
                      fontSize: 2,
                    }}
                  >
                    <Box
                      as="input"
                      type="radio"
                      name="shareMethod"
                      checked={shareMethod === "manual"}
                      onChange={() => setShareMethod("manual")}
                      sx={{ accentColor: "primary.base", width: 18, height: 18 }}
                    />
                    Manually copy link
                  </Box>
                </Flex>
              </Box>

              <Flex
                sx={{
                  mb: 3,
                  alignItems: "flex-start",
                  gap: 2,
                  bg: "rgba(0, 149, 255, 0.08)",
                  borderLeft: "4px solid",
                  borderLeftColor: "info.base",
                  borderRadius: PAY.radiusSm,
                  px: 3,
                  py: 3,
                  color: "neutral.d1",
                  fontSize: 2,
                }}
              >
                <Icon
                  variant="color-info"
                  size="sm"
                  sx={{ flexShrink: 0, mt: "2px" }}
                />
                <Box>
                  {shareMethod === "email" ? (
                    <>
                      We&apos;ll email the invoice to{" "}
                      <Box as="strong" sx={{ fontWeight: 700, color: "neutral.d1" }}>
                        {form.email.trim() || "the customer"}
                      </Box>{" "}
                      with a Pay invoice link.
                    </>
                  ) : (
                    "You'll copy the invoice link in the next step and send it however you like"
                  )}
                </Box>
              </Flex>

              {submitError ? (
                <Box sx={{ mb: 2, color: "danger.base" }}>{submitError}</Box>
              ) : null}
              <Flex sx={{ justifyContent: "flex-end", mt: 1 }}>
                <Button
                  type="button"
                  onClick={handleGenerateInvoiceLink}
                  disabled={submitting || !actor}
                  sx={primaryButtonSx}
                >
                  {submitting
                    ? shareMethod === "email"
                      ? "Sending…"
                      : "Generating…"
                    : shareMethod === "email"
                      ? "Send Invoice Email"
                      : "Generate invoice link"}
                </Button>
              </Flex>
            </Box>
          )}

          {invoiceResult && invoiceLink && (
            <Box sx={{ maxWidth: 720, mx: "auto", width: "100%" }}>
              <Flex
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                  mb: 4,
                  textAlign: "center",
                }}
              >
                <Icon
                  variant="check-circle"
                  size="md"
                  sx={{ color: "#15803d", flexShrink: 0 }}
                />
                <Heading variant="h5" as="h3" sx={{ fontSize: [3, 4], m: 0 }}>
                  {shareMethod === "email"
                    ? "Done! Email sent!"
                    : linkCopied
                      ? "Invoice link created and copied!"
                      : "Invoice link created!"}
                </Heading>
              </Flex>

              <Box sx={cardSx}>
                <Flex sx={{ alignItems: "center", gap: 2, mb: 2 }}>
                  <Icon variant="attachment" size="sm" sx={{ color: "neutral.d2" }} />
                  <Box sx={{ fontWeight: 600, fontSize: 2 }}>Invoice link</Box>
                </Flex>
                <Flex sx={{ gap: 2, flexWrap: "wrap", alignItems: "stretch" }}>
                  <Box
                    as="input"
                    readOnly
                    value={invoiceLink}
                    onClick={(e) => e.target.select?.()}
                    sx={{ ...fieldSx, flex: "1 1 240px", bg: "neutral.l4" }}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={async () => {
                      const copied = await copyTextToClipboard(invoiceLink);
                      setLinkCopied(copied);
                    }}
                    sx={{
                      ...secondaryButtonSx,
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      color: "primary.base",
                      borderColor: "primary.base",
                    }}
                  >
                    <Icon variant="clipboard" size="sm" />
                    {linkCopied ? "Copied" : "Copy"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      window.open(invoiceLink, "_blank", "noopener,noreferrer")
                    }
                    sx={{
                      ...secondaryButtonSx,
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      color: "primary.base",
                      borderColor: "primary.base",
                    }}
                  >
                    <Icon variant="open-new-tab" size="sm" />
                    Open
                  </Button>
                </Flex>
              </Box>

              <Box sx={{ ...cardSx, mb: 4 }}>
                <Flex sx={{ flexDirection: "column", gap: 2, mb: 3 }}>
                  <SummaryDetailRow icon="user">
                    {`${form.firstName} ${form.lastName}`.trim() || "—"}
                  </SummaryDetailRow>
                  <SummaryDetailRow icon="envelope">
                    {form.email.trim() || "—"}
                  </SummaryDetailRow>
                  {hasAddressInput ? (
                    <SummaryDetailRow icon="home">
                      {formatAddress(form)}
                    </SummaryDetailRow>
                  ) : null}
                  {(form.referenceId.trim() ||
                    transactionResult?.referenceNumber ||
                    invoiceResult?.referenceNumber) && (
                    <SummaryDetailRow icon="clipboard">
                      {form.referenceId.trim() ||
                        transactionResult?.referenceNumber ||
                        invoiceResult?.referenceNumber}
                    </SummaryDetailRow>
                  )}
                </Flex>

                <Box
                  sx={{
                    bg: "rgba(0, 131, 143, 0.08)",
                    borderRadius: PAY.radiusSm,
                    p: 3,
                  }}
                >
                  <Box sx={{ fontWeight: 600, fontSize: 2, mb: 2 }}>
                    Order summary
                  </Box>
                  <Flex
                    sx={{
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                      color: "neutral.d1",
                      fontSize: 2,
                    }}
                  >
                    <Icon variant="payment" size="sm" />
                    <Box>Custom charge</Box>
                  </Flex>
                  <Flex
                    sx={{
                      justifyContent: "space-between",
                      gap: 2,
                      mb: 2,
                      color: "neutral.d1",
                      fontSize: 2,
                    }}
                  >
                    <Box sx={{ wordBreak: "break-word" }}>
                      {form.description.trim() || "—"}
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      {amountParsed != null
                        ? numberToMoney2(amountParsed)
                        : "—"}
                    </Box>
                  </Flex>
                  <Box
                    sx={{
                      borderTop: "1px solid",
                      borderColor: "neutral.l3",
                      pt: 2,
                    }}
                  />
                  <Flex
                    sx={{
                      justifyContent: "space-between",
                      fontWeight: 700,
                      mt: 2,
                      fontSize: 2,
                    }}
                  >
                    <span>Invoice total</span>
                    <span>
                      {amountParsed != null
                        ? numberToMoney2(amountParsed)
                        : "—"}
                    </span>
                  </Flex>
                </Box>
              </Box>

              <Flex sx={{ justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  onClick={clearForm}
                  sx={{ ...primaryButtonSx, minWidth: 140 }}
                >
                  Done
                </Button>
              </Flex>
            </Box>
          )}
        </Box>

        {showNextSteps && (
          <Box
            sx={{
              width: ["100%", "100%", 320],
              flexShrink: 0,
              border: "1px solid",
              borderColor: "neutral.l3",
              borderRadius: PAY.radius,
              p: 3,
              position: ["static", "static", "sticky"],
              top: 24,
              bg: "white",
              boxShadow: PAY.cardShadow,
            }}
          >
            <Heading variant="h6" as="h3" sx={{ mb: 3, fontWeight: 600 }}>
              Next steps
            </Heading>

            <NextStepBlock
              status="complete"
              title="Start new payment"
              onEdit={() => setActiveStep(0)}
            >
              <Flex sx={{ flexDirection: "column", gap: 2 }}>
                <SummaryDetailRow icon="user">
                  {`${form.firstName} ${form.lastName}`.trim() || "—"}
                </SummaryDetailRow>
                <SummaryDetailRow icon="envelope">
                  {form.email.trim() || "—"}
                </SummaryDetailRow>
                {hasAddressInput ? (
                  <SummaryDetailRow icon="home">
                    {formatAddress(form)}
                  </SummaryDetailRow>
                ) : null}
                {form.referenceId.trim() ? (
                  <SummaryDetailRow icon="clipboard">
                    {form.referenceId.trim()}
                  </SummaryDetailRow>
                ) : null}
              </Flex>
            </NextStepBlock>

            <NextStepBlock
              status={
                amountParsed != null && form.description.trim() && activeStep > 1
                  ? "complete"
                  : activeStep === 1
                    ? "active"
                    : "pending"
              }
              title="Build Your Order"
              onEdit={
                amountParsed != null && form.description.trim() && activeStep > 1
                  ? () => setActiveStep(1)
                  : undefined
              }
            >
              {amountParsed != null && form.description.trim() ? (
                <Box sx={{ fontSize: 2 }}>
                  <Flex sx={{ alignItems: "center", gap: 2, mb: 2, color: "neutral.d1" }}>
                    <Icon variant="payment" size="sm" />
                    <Box>Custom charge</Box>
                  </Flex>
                  <Flex
                    sx={{
                      justifyContent: "space-between",
                      gap: 2,
                      mb: 2,
                      pl: "28px",
                      color: "neutral.d1",
                    }}
                  >
                    <Box sx={{ wordBreak: "break-word" }}>
                      {form.description.trim()}
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      {numberToMoney2(amountParsed)}
                    </Box>
                  </Flex>
                  <Box
                    sx={{
                      borderTop: "1px solid",
                      borderColor: "neutral.l3",
                      pt: 2,
                    }}
                  />
                  <Flex
                    sx={{
                      justifyContent: "space-between",
                      fontWeight: 700,
                      mt: 2,
                    }}
                  >
                    <span>Total</span>
                    <span>{numberToMoney2(amountParsed)}</span>
                  </Flex>
                </Box>
              ) : null}
            </NextStepBlock>

            <NextStepBlock
              status={activeStep === 2 ? "active" : "pending"}
              title="Create Invoice"
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
}
